import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import { fetchLegalAdviceById, saveLegalAdviceDraft, submitLegalAdviceForm } from '../../../services/LegalAdviceTwoService';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
import type { LegalAdviceFormTwoData } from '../../../types/LegalAdvices/LegalAdviceFormTwo.type';

export const INITIAL_LEGAL_ADVICE_FORM_DATA: LegalAdviceFormTwoData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Legal Advice specific form fields
  office: '',
  caseYear: '',
  caseNo: '',
  date: '',
  state: '',
  district: '',
  township: '',

  complaintNo: '', // သတင်းပေး
  refNo: '',       // တိုင်ကြားချက်အမှတ်
  lawSection: '',
  accusedName: '',

  officerState: '',
  officerDistrict: '',
  officerTownship: '',

  courtName: '',
  miscCaseNo: '',

  fatherName: '',

  signatureImage: '',
  signName: '',
  signerName: '',

  lawOffice: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormTwoData>;
}

export function LegalFormTwo({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormTwoData>({
    ...INITIAL_LEGAL_ADVICE_FORM_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  const STATE_OPTIONS = settings?.states.map((t) => t.name) || [];
  const DISTRICT_OPTIONS = settings?.districts.map((t) => t.name) || [];
  const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceById(id)
      .then((data: LegalAdviceFormTwoData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.signatureImage?.trim() && 
    (formData.signerName?.trim() || formData.signName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormTwo'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-one');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearDate = (name: keyof LegalAdviceFormTwoData) => {
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, [name]: '' }));
  };

  const handleClearSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImage: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
        return;
      }
      try {
        const canvas = sigCanvasRef.current.getTrimmedCanvas();
        const base64Data = canvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      } catch {
        const rawCanvas = sigCanvasRef.current.getCanvas();
        const base64Data = rawCanvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      }
    }
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormTwoData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.date?.trim()) {
      alert('ရက်စွဲ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    const nameOfSigner = (formData.signerName || formData.signName || '').trim();
    if (!formData.signatureImage?.trim() || !nameOfSigner) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormTwoData = {
        ...formData,
        id: targetId || '',
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.date?.trim()) {
      alert('ရက်စွဲ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
      const levelsToSubmit =
        targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

      // Validate that at least one checkbox level is selected
      if (!levelsToSubmit || levelsToSubmit.length === 0) {
        alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
        return;
      }


    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const nameOfSigner = (formData.signerName || formData.signName || '').trim();
      const hasSigned = Boolean(formData.signatureImage?.trim() && nameOfSigner);
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';

      if(!hasSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }

      const payload: LegalAdviceFormTwoData = {
        ...formData,
        id: targetId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'SUBMITTED',
        submitDate: formData.date,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      //navigate('/legal-form-one');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDatePicker = (
    name: Extract<keyof LegalAdviceFormTwoData, string>
  ) => (
    <FormDatePicker
      name={name}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
    const status = formData.status || 'DRAFT';

    // Extract and format Geo Level text
    const rawGeo = formData.targetGeoLevels || formData.geoLevel;
    const formatGeo = (geo: any) => {
      if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
      if (Array.isArray(geo)) {
        return geo
          .map((g) => GEO_LEVEL_MAP[g] || g)
          .filter(Boolean)
          .join(' | ');
      }
      return '';
    };
    const geoText = formatGeo(rawGeo);

    const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
    <PageLayoutWrapper
      handleBackNavigation={handleBackNavigation}
      renderStageProgression={renderStageProgression}
      isEditable={isEditable}
      isPreview={isPreview}
      readOnlyMessage={
        formData.status === 'APPROVED'
          ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
          : formData.status === 'SUBMITTED'
            ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : undefined
      }
      setIsPreview={setIsPreview}
    >
      {/* A4 Sheet Container */}
      <div className="w-full max-w-[800px] bg-white shadow-lg border border-gray-300 p-16 text-black leading-loose relative text-sm md:text-base print:shadow-none print:border-none print:p-0 print:max-w-full">
        
        {/* Right Top Form Label Tag */}
        <div className="absolute top-6 right-8 text-sm font-bold text-right space-y-0.5 print:top-0 print:right-0">
          <div>ဥပရအမှုလိုက်ပုံစံ ( ၂ )</div>
        </div>

        {/* Central National Document Headers */}
        <div className="text-center space-y-1 mt-8">
          <h1 className="text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
          <h2 className="text-md font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
          {/* <div className="text-sm font-bold">မှ</div> */}
          <div className="flex justify-center items-center gap-2">
            <DatalistInput
              name="office"
              value={formData.office || ""}
              onChange={handleChange}
              placeholder=""
              options={OFFICE_OPTIONS}
              disabled={!isEditable}
            />
            <span className="w-24 text-left">ရုံး</span>
          </div>
        </div>

        {/* Reference Case Identity Line decoration */}
        <div className="mt-6 flex flex-col items-center space-y-4">
          <div className="flex items-center justify-center text-center w-full max-w-xl leading-8 flex-wrap">
            {isPreview ? (
              <span className="min-w-[5rem] border-b border-black text-center font-semibold px-2">{formData.caseYear || '....'}</span>
            ) : (
              <input 
                type="text" 
                name="caseYear"
                value={formData.caseYear}
                onChange={handleChange}
                className="w-20 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
              />
            )}
            <span className="mx-1">ခုနှစ်၊ အစိုးရသက်သေပြုလုပ်ခြင်း၊ အမှုတွဲအမှတ် (</span>
            {isPreview ? (
              <span className="min-w-[5rem] border-b border-black text-center font-semibold px-2">{formData.caseNo || '....'}</span>
            ) : (
              <input 
                type="text" 
                name="caseNo"
                value={formData.caseNo}
                onChange={handleChange}
                className="w-20 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
              />
            )}
            <span className="mx-1">)</span>
          </div>

          {/* Unified Form Calendar Picker */}
          <div className="flex items-center gap-2 w-64 justify-end">
            <span className="whitespace-nowrap">ရက်စွဲ၊</span>
            <div className="flex-grow">{renderDatePicker('date')}</div>
          </div>
        </div>

        {/* Centered Document Title */}
        <div className="text-center mt-10 mb-8">
          <h2 className="text-base font-bold tracking-wide underline underline-offset-8 decoration-1">
            တရားခံအား စည်းကမ်းချက်ဖြင့် ပြစ်ဒဏ်မှ လွတ်ငြိမ်းခွင့် ပေးသည့်အမိန့်
          </h2>
        </div>

        {/* Main Form Body structures */}
        <div className="space-y-8 text-justify leading-9">
          
          {/* Paragraph 1 */}
          <div className="relative pl-8">
            <span className="absolute left-0 top-0 font-bold">၁။</span>
            <div className="leading-9">
              <DatalistInput
                name="state"
                value={formData.state || ""}
                onChange={handleChange}
                placeholder=""
                options={STATE_OPTIONS}
                disabled={!isEditable}
                className="inline-block w-32 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
              />
              <span> ပြည်နယ်/တိုင်းဒေသကြီး၊ </span>
              <DatalistInput
                name="district"
                value={formData.district || ""}
                onChange={handleChange}
                placeholder=""
                options={DISTRICT_OPTIONS}
                disabled={!isEditable}
                className="inline-block w-24 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
              />
              <span> ခရိုင် </span>
              <DatalistInput
                name="township"
                value={formData.township || ""}
                onChange={handleChange}
                placeholder=""
                options={TOWNSHIP_OPTIONS}
                disabled={!isEditable}
                className="inline-block w-32 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
              />
              <span> မြို့နယ် ရဲတပ်ဖွဲ့မှူးက အစီရင်ခံစာတင်သွင်းသည်။ </span>
              {isPreview ? (
                <span className="inline-block min-w-12 border-b border-black px-1 text-center font-semibold">{formData.complaintNo}</span>
              ) : (
                <input
                  type="text"
                  name="complaintNo"
                  value={formData.complaintNo}
                  onChange={handleChange}
                  className="inline-block w-12 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
                />
              )}
              <span> ရဲစခန်း၊ ပထမသတင်းပေး တိုင်တန်းချက်အမှတ် (ပ) </span>
              {isPreview ? (
                <span className="inline-block min-w-12 border-b border-black px-1 text-center font-semibold">{formData.refNo}</span>
              ) : (
                <input
                  type="text"
                  name="refNo"
                  value={formData.refNo}
                  onChange={handleChange}
                  className="inline-block w-12 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
                />
              )}
              <span> ဥပဒေပုဒ်မ </span>
              {isPreview ? (
                <span className="inline-block min-w-20 border-b border-black px-1 text-center font-semibold">{formData.lawSection}</span>
              ) : (
                <input
                  type="text"
                  name="lawSection"
                  value={formData.lawSection}
                  onChange={handleChange}
                  className="inline-block w-20 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
                />
              )}
              <span> အရ အရေးယူခံရသူ </span>
              {isPreview ? (
                <span className="inline-block min-w-24 border-b border-black px-1 text-center font-semibold">{formData.accusedName}</span>
              ) : (
                <input
                  type="text"
                  name="accusedName"
                  value={formData.accusedName}
                  onChange={handleChange}
                  className="inline-block w-24 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
                />
              )}
              <span> အား ရာဇဝတ်ကျင့်ထုံးဥပဒေပုဒ်မ ၃၃၇ အရ စည်းကမ်းချက်ဖြင့် ပြစ်ဒဏ်မှ လွတ်ငြိမ်းခွင့်ပေးရန် </span>
              <DatalistInput
                name="officerState"
                value={formData.officerState || ""}
                onChange={handleChange}
                placeholder=""
                options={STATE_OPTIONS}
                disabled={!isEditable}
                className="inline-block w-32 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
              />
              <span> ပြည်နယ်/တိုင်းဒေသကြီး၊ </span>
              <DatalistInput
                name="officerDistrict"
                value={formData.officerDistrict || ""}
                onChange={handleChange}
                placeholder=""
                options={DISTRICT_OPTIONS}
                disabled={!isEditable}
                className="inline-block w-24 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
              />
              <span> ခရိုင်၊ </span>
              <DatalistInput
                name="officerTownship"
                value={formData.officerTownship || ""}
                onChange={handleChange}
                placeholder=""
                options={TOWNSHIP_OPTIONS}
                disabled={!isEditable}
                className="inline-block w-32 align-baseline bg-transparent border-b border-dotted border-black text-center focus:outline-none"
              />
              <span> မြို့နယ်ရဲတပ်ဖွဲ့မှူးက အစီရင်ခံစာတင်သွင်းသည်။</span>
            </div>
          </div>

          {/* Paragraph 2 */}
          <div className="relative pl-8">
            <span className="absolute left-0 top-0 font-bold">၂။</span>
            <div className="inline">
              <span>အဆိုပါအမှုနှင့်စပ်လျဉ်း၍</span>
              
              {isPreview ? (
                <span className="border-b border-black font-semibold px-4 mx-1 min-w-[8rem] inline-block text-center">{formData.courtName || '.......'}</span>
              ) : (
                <input 
                  type="text" 
                  name="courtName"
                  value={formData.courtName}
                  onChange={handleChange}
                  placeholder="တရားရုံး"
                  className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
                />
              )}

              <span>တရားရုံး၊ ပြစ်မှုဆိုင်ရာ အထွေထွေမှုအမှတ်</span>
              
              {isPreview ? (
                <span className="border-b border-black font-semibold px-4 mx-1 min-w-[8rem] inline-block text-center">{formData.miscCaseNo || '.......'}</span>
              ) : (
                <input 
                  type="text" 
                  name="miscCaseNo"
                  value={formData.miscCaseNo}
                  onChange={handleChange}
                  className="w-40 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
                />
              )}

              <span>တွင် တရားခံက ပေးထားသော ဖြောင့်ချက် သို့မဟုတ် ဥပဒေရုံးတွင် တရားခံထွက်ဆိုထားသော ဝန်ခံချက်ကို ၎င်းအား ဖတ်ပြရာ ဖြောင့်ချက်/ဝန်ခံချက်သည် ဟုတ်မှန် ကြောင်းနှင့် ၎င်း၏ ဆန္ဒအတိုင်းအလိုအလျောက် ပေးထားကြောင်းဝန်ခံသည်။ ဖြောင့်ချက်/ဝန်ခံချက်ပါ အချက် အလက်များကို တရားရုံးရှေ့တွင် အလုံးစုံ မခြွင်းမချန်အမှန်အတိုင်း ထွက်ဆိုပါမည်ဟူသော စည်းကမ်းချက် ဖြင့် တရားခံအား ပြစ်ဒဏ်မှ လွတ်ငြိမ်းခွင့်ပေးသင့်သည်ဟု ယူဆသည်။</span>
            </div>
          </div>

          {/* Paragraph 3 */}
          <div className="relative pl-8 pt-4">
            <span className="absolute left-0 top-4 font-bold">၃။</span>
            <div className="inline">
              <span>သို့ဖြစ်ပါ၍ ဦး</span>
              
              {isPreview ? (
                <span className="border-b border-black font-semibold px-4 mx-1 min-w-[10rem] inline-block text-center">{formData.fatherName || '.......'}</span>
              ) : (
                <input 
                  type="text" 
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  className="w-44 text-center bg-transparent border-b border-dotted border-black focus:outline-none mx-1"
                />
              )}

              <span>၏ သား/သမီးဖြစ်သူ တရားခံ</span>
              
              {isPreview ? (
                <span className="border-b border-black font-semibold px-4 mx-1 min-w-[6rem] inline-block text-center">{formData.accusedName || '.......'}</span>
              ) : (
                <input 
                  type="text" 
                  name="accusedName"
                  value={formData.accusedName}
                  onChange={handleChange}
                  placeholder=""
                  className="w-20 text-center bg-transparent border-b border-dotted border-black focus:outline-none"
                />
              )}

              <span>အား ရာဇဝတ်ကျင့်ထုံးဥပဒေပုဒ်မ ၃၃၇ အရ အစိုးရသက်သေပြုလုပ်ရန် စည်းကမ်းချက်ဖြင့် ပြစ်ဒဏ်မှ လွတ်ငြိမ်းခွင့် ပေးလိုက်သည်။</span>
            </div>
          </div>

        </div>

        {/* Signature Box Block */}
        <div className="mt-16 flex flex-col items-end justify-end text-sm md:text-base print:mt-12">
          <div className="w-80 flex flex-col space-y-2">
            
            {/* Signature Box Canvas Trigger */}
            <div className="flex items-center">
                <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
                <div
                  onClick={() => isEditable && setIsSigning(true)}
                  className={`flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center relative group ${
                    isEditable ? "cursor-pointer hover:bg-gray-50 transition" : ""
                  }`}
                >
                  {formData.signatureImage ? (
                    <>
                      <img
                        src={formData.signatureImage}
                        alt="Signature"
                        className="h-8 object-contain mix-blend-multiply"
                      />
                      {isEditable && (
                        <span className="absolute inset-0 bg-black/40 text-white text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded print:hidden">
                          လက်မှတ်အသစ်ထိုးရန် နှိပ်ပါ
                        </span>
                      )}
                    </>
                  ) : (
                    isEditable && (
                      <span className="text-gray-400 text-xs font-sans print:hidden">
                        ✍️ ဒီနေရာတွင် လက်မှတ်ထိုးရန် နှိပ်ပါ
                      </span>
                    )
                  )}
                  {formData.signatureImage && (
                    <button
                      type="button"
                      onClick={handleClearSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>              
              </div>

            {/* Name Form Box */}
            <div className="flex items-center">
              <span className="w-16 text-right whitespace-nowrap mr-2">အမည်</span>
              {isPreview ? (
                <span className="flex-1 text-center font-semibold border-b border-black min-h-[24px]">{formData.signName}</span>
              ) : (
                <input
                  type="text"
                  name="signName"
                  value={formData.signName}
                  onChange={handleChange}
                  className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center"
                />
              )}
            </div>

            {/* Role Title */}
            <div className="text-center font-bold text-sm pt-1 pl-12">
              တာဝန်ခံဥပဒေအရာရှိ
            </div>

            {/* Law Office Control */}
            <div className="flex items-end justify-end">
              <div className="flex justify-center items-center gap-2">
                <DatalistInput
                  name="lawOffice"
                  value={formData.lawOffice || ""}
                  onChange={handleChange}
                  placeholder=""
                  options={OFFICE_OPTIONS}
                  disabled={!isEditable}
                />
                <span className="w-24 text-left">ဥပဒေရုံး</span>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom Page Number Indicators */}
        <div className="absolute bottom-6 left-8 text-xs text-gray-400 print:bottom-0 print:left-0">၁၂၀</div>

        {/* Action Buttons */}
        {/* Form Action Buttons Component Integration */}
          <FormActionButtons
            isPreview={isPreview}
            isEditable={isEditable}
            isSubmitting={isSubmitting}
            isFormSigned={isFormSigned}
            isApproved={isApproved}
            targetGeoLevels={formData.targetGeoLevels}
            currentGeoLevel={currentUser?.scopeLevel}
            selectedGeoLevels={selectedGeoLevels}
            onGeoLevelChange={setSelectedGeoLevels}
            onCancel={handleBackNavigation}
            onSaveDraft={handleSaveDraft}
            onApproveAndSave={handleApproveAndSave}
            onSubmitToSuperior={handleSubmitToSuperior}
          />

        {/* Attachment Section */}
        {/* <AttachmentSection
          attachments={formData.attachments}
          isEditable={isEditable}
          isPreview={isPreview}
          onFileChange={handleFileChange}
          onRemoveFile={handleRemoveFile}
        /> */}

        {/* Signature Modal */}
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => setIsSigning(false)}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleSaveSignature}
        />

        <div className="absolute bottom-4 left-4 text-xs text-gray-400 print:bottom-0 print:left-0">
          လျှို့ဝှက်
        </div>
      
      </div>
    </PageLayoutWrapper>
  );
}